import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { httpClient, type ApiError, type Paginated } from '@/lib/api';
import { queryKeys } from '@/lib/query/query-keys';

import type {
  LeaveBalance,
  LeaveCreateInput,
  LeaveRequest,
  LeaveRequestFilters,
  SatisfactionContext,
  SatisfactionSubmission,
  Timesheet,
  TimesheetDayInput,
  TimesheetOptions,
  TimesheetOvertimeInput,
} from './types';

/* ── Lectures ─────────────────────────────────────────────────────────────── */

/**
 * Référentiels de la saisie.
 *
 * `staleTime` très long : ces listes ne changent qu'à l'administration d'un
 * référentiel, alors que la grille les interroge à chaque ouverture de mois.
 */
export const useTimesheetOptions = (): UseQueryResult<TimesheetOptions, ApiError> =>
  useQuery<TimesheetOptions, ApiError>({
    queryKey: queryKeys.timesheets.options(),
    queryFn: () => httpClient.get<TimesheetOptions>('/timesheets/options'),
    staleTime: 30 * 60 * 1_000,
  });

/**
 * Les CRA du consultant.
 *
 * Aucun filtre par personne n'est transmis : le serveur restreint de lui-même
 * un compte de portail à ses propres déclarations. En passer un donnerait
 * l'illusion que le cloisonnement vient du client.
 */
export const useMyTimesheets = (
  filters: { missionId?: string; periodMonth?: string } = {},
  enabled = true,
): UseQueryResult<Paginated<Timesheet>, ApiError> =>
  useQuery<Paginated<Timesheet>, ApiError>({
    enabled,
    queryKey: queryKeys.timesheets.list({ mine: true, ...filters }),
    queryFn: () =>
      httpClient.getPaginated<Timesheet>('/timesheets', {
        query: { limit: 50, sortBy: 'periodMonth', sortOrder: 'desc', ...filters },
      }),
  });

export const useTimesheet = (id: string | undefined): UseQueryResult<Timesheet, ApiError> =>
  useQuery<Timesheet, ApiError>({
    queryKey: queryKeys.timesheets.detail(id ?? 'none'),
    queryFn: () => httpClient.get<Timesheet>(`/timesheets/${id ?? ''}`),
    enabled: Boolean(id),
  });

/**
 * Questionnaire de satisfaction à remplir avant de soumettre.
 *
 * Chargé seulement quand on s'apprête à soumettre (`enabled`) : le serveur
 * refuse la requête si le CRA n'est pas dans un état qui le permet, et une
 * erreur affichée à l'ouverture de la fiche n'aurait aucun sens.
 */
export const useSatisfaction = (
  id: string | undefined,
  enabled: boolean,
): UseQueryResult<SatisfactionContext, ApiError> =>
  useQuery<SatisfactionContext, ApiError>({
    queryKey: queryKeys.timesheets.satisfaction(id ?? 'none', 'SUBMISSION'),
    queryFn: () =>
      httpClient.get<SatisfactionContext>(`/timesheets/${id ?? ''}/satisfaction`, {
        query: { stage: 'SUBMISSION' },
      }),
    enabled: Boolean(id) && enabled,
  });

/* ── Écritures ────────────────────────────────────────────────────────────── */

/**
 * Dépose la réponse dans le cache de la fiche et invalide les listes.
 *
 * Toutes les écritures renvoient le CRA **relu**, totaux recalculés : l'écran
 * se met à jour sans seconde requête, et la liste — qui porte l'état et les
 * totaux — cesse d'être périmée.
 */
const useTimesheetWrite = <TInput>(
  mutationFn: (input: TInput) => Promise<Timesheet>,
): UseMutationResult<Timesheet, ApiError, TInput> => {
  const queryClient = useQueryClient();

  return useMutation<Timesheet, ApiError, TInput>({
    mutationFn,
    onSuccess: (timesheet) => {
      queryClient.setQueryData(queryKeys.timesheets.detail(timesheet.id), timesheet);
      void queryClient.invalidateQueries({ queryKey: queryKeys.timesheets.lists() });
    },
  });
};

export const useOpenTimesheet = (): UseMutationResult<
  Timesheet,
  ApiError,
  { missionId: string; periodMonth: string }
> =>
  useTimesheetWrite((input: { missionId: string; periodMonth: string }) =>
    httpClient.post<Timesheet>('/timesheets', input),
  );

/**
 * Remplace le mois **en bloc**.
 *
 * `PUT` et non `PATCH` : le corps décrit le mois complet après modification.
 * Une fusion ligne à ligne obligerait à transmettre des suppressions, et un
 * jour oublié resterait déclaré sans que personne le voie.
 */
export const useReplaceDays = (): UseMutationResult<
  Timesheet,
  ApiError,
  { id: string; days: TimesheetDayInput[] }
> =>
  useTimesheetWrite(({ id, days }: { id: string; days: TimesheetDayInput[] }) =>
    httpClient.put<Timesheet>(`/timesheets/${id}/days`, { days }),
  );

/** Même déclaration et recalcul des totaux que dans le CRA interne. */
export const useReplaceOvertimes = () =>
  useTimesheetWrite(({ id, overtimes }: { id: string; overtimes: TimesheetOvertimeInput[] }) =>
    httpClient.put<Timesheet>(`/timesheets/${id}/overtimes`, { overtimes }),
  );

/** La soumission exige le questionnaire : le serveur refuse un corps sans lui. */
export const useSubmitTimesheet = (): UseMutationResult<
  Timesheet,
  ApiError,
  { id: string; satisfaction: SatisfactionSubmission }
> =>
  useTimesheetWrite(({ id, satisfaction }: { id: string; satisfaction: SatisfactionSubmission }) =>
    httpClient.post<Timesheet>(`/timesheets/${id}/submit`, { satisfaction }),
  );

/* ── Congés ───────────────────────────────────────────────────────────────── */

/** Le serveur limite toujours les demandes au consultant authentifié. */
export const useLeaveRequests = (filters: LeaveRequestFilters, enabled: boolean) =>
  useQuery<Paginated<LeaveRequest>, ApiError>({
    queryKey: queryKeys.leaves.list(filters),
    queryFn: () =>
      httpClient.getPaginated<LeaveRequest>('/leave-requests', { query: { ...filters } }),
    enabled,
  });

export const useMyLeaves = (): UseQueryResult<Paginated<LeaveRequest>, ApiError> =>
  useQuery<Paginated<LeaveRequest>, ApiError>({
    queryKey: queryKeys.leaves.list({ mine: true }),
    queryFn: () =>
      httpClient.getPaginated<LeaveRequest>('/leave-requests', {
        query: { limit: 50, sortBy: 'startDate', sortOrder: 'desc' },
      }),
  });

export const useLeaveBalance = (
  missionId: string | undefined,
): UseQueryResult<LeaveBalance, ApiError> =>
  useQuery<LeaveBalance, ApiError>({
    queryKey: queryKeys.leaves.balance(missionId ?? 'none'),
    queryFn: () => httpClient.get<LeaveBalance>(`/leave-requests/balance/${missionId ?? ''}`),
    enabled: Boolean(missionId),
  });

export const useCreateLeave = (): UseMutationResult<LeaveRequest, ApiError, LeaveCreateInput> => {
  const queryClient = useQueryClient();

  return useMutation<LeaveRequest, ApiError, LeaveCreateInput>({
    mutationFn: (input) => httpClient.post<LeaveRequest>('/leave-requests', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.leaves.all });
      /* Un congé accepté se posera dans un CRA : la liste peut changer. */
      void queryClient.invalidateQueries({ queryKey: queryKeys.timesheets.all });
    },
  });
};
